/**
 * Density (proposal decision 7) — two tokens and a persisted choice, not an alternative theme.
 *
 * The same 52px row is right on a 13" laptop and wastes half a 27" screen in front of an operator
 * who wants more rows on it. The whole mechanism is `data-density` on `<html>`, which switches
 * `--density-row-h`, `--density-pad` and `--density-cell-y` in `globals.css`; nothing else in the
 * codebase reads this value, and no component branches on it.
 *
 * It is a LOCAL preference, stored only in this browser. It is not written through to
 * `PATCH /v1/profile` the way theme and language are, because no `density_preference` column
 * exists and adding one is a migration in a platform service for a display choice — the standard's
 * own rule is that the user's explicit preference beats the automatic behaviour, and localStorage
 * satisfies that. If it should follow an operator between devices, that is a backend change and
 * this module is where the write-through would attach (see `theme.ts`'s pending-sync trio for the
 * pattern it would copy).
 */
export type Density = 'comfortable' | 'compact';

export const DENSITY_STORAGE_KEY = 'dgb-console:density';

/** `comfortable` is the default: the touch-first row height, and the one a first-time user gets. */
export const DEFAULT_DENSITY: Density = 'comfortable';

const DENSITIES: readonly Density[] = ['comfortable', 'compact'];

export function isDensity(value: unknown): value is Density {
  return typeof value === 'string' && (DENSITIES as readonly string[]).includes(value);
}

/** Defensive read, mirroring `theme.ts`'s `readStoredMode`: a browser with storage blocked (or a
 * private window) must fall back to the default rather than throw before first paint. */
export function readStoredDensity(): Density {
  try {
    const raw = localStorage.getItem(DENSITY_STORAGE_KEY);
    return isDensity(raw) ? raw : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

export function writeStoredDensity(density: Density): void {
  try {
    localStorage.setItem(DENSITY_STORAGE_KEY, density);
  } catch {
    // No-op: the choice still applies to this session, it just does not survive a reload.
  }
}

/**
 * The attribute the CSS reads. `comfortable` REMOVES the attribute rather than writing
 * `data-density="comfortable"`, so the default state carries no attribute at all and the CSS needs
 * exactly one selector (`:root[data-density='compact']`) instead of two that must stay in sync.
 */
export function applyDensity(density: Density): void {
  if (density === 'compact') {
    document.documentElement.setAttribute('data-density', 'compact');
  } else {
    document.documentElement.removeAttribute('data-density');
  }
}
